/**
 * ListViewTabPanel
 *
 * 사업별/부서별 현황의 "목록보기" 액션에서 여는 통합 목록 사이드패널.
 *
 * MyTaskDashboard 요약 클릭 시 뜨는 목록 사이드패널과 동일한 컨셉이지만,
 * 유형(수행업무/세부실행과제/예산/성과)별로 다른 패널을 여는 대신
 * 하나의 패널 안에서 탭으로 유형을 오갈 수 있다.
 *
 * 레이아웃:
 *   SidePanel
 *     ├─ ListPanelFilterBar  (상단 공유 필터 영역: 상태/사업/부서/담당자/검색)
 *     ├─ 탭 바               (수행업무 | 세부실행과제 | 예산 | 성과)
 *     └─ 활성 탭의 목록 테이블
 *
 * 필터: 부모가 prop으로 넘긴 필터(대시보드 필터 + 클릭한 행의 사업/부서)는 lock(disabled)으로 표시.
 * 패널 안에서 사용자가 변경한 필터는 패널-로컬 state로만 적용 (부모 page state는 영향 없음).
 * 상태(status) 옵션은 탭마다 다르므로 탭 전환 시 초기화된다.
 * 세부실행과제 탭에서만 성과상태 셀렉트를 함께 보여준다 (상태와 AND).
 */

import React, { useEffect, useState } from 'react'
import SidePanel from '@/components/ui/SidePanel'
import ListPanelFilterBar from './ListPanelFilterBar'
import TaskItemsTable from './TaskItemsTable'
import TasksTable from './TasksTable'
import TaskBudgetsTable from './TaskBudgetsTable'
import TaskPerformancesTable from './TaskPerformancesTable'
import { designTokens } from '@/utils/design-tokens'
import { useShowTasks } from '@/hooks/useShowTasks'
import type { TaskTreeNode } from '@/pages/project/TaskManagement/components/TaskHierarchyTable'

type ListViewTab = 'task_item' | 'task' | 'budget' | 'performance'

interface TabDef {
  key: ListViewTab
  label: string
  statusOptions: { value: string; label: string }[]
}

const TABS: TabDef[] = [
  {
    key: 'task_item',
    label: '수행업무',
    statusOptions: [
      { value: 'incomplete', label: '미완료 (담당자)' },
      { value: 'assigned', label: '미완료 (관리자)' },
      { value: 'completed', label: '완료 (관리자)' },
      { value: 'reviewed', label: '완료 (검토자)' },
    ],
  },
  {
    key: 'task',
    label: '세부실행과제',
    statusOptions: [
      { value: 'incomplete', label: '미완료' },
      { value: 'completed', label: '완료' },
    ],
  },
  {
    key: 'budget',
    label: '예산',
    statusOptions: [
      { value: 'incomplete', label: '미완료' },
      { value: 'completed', label: '완료' },
    ],
  },
  {
    key: 'performance',
    label: '성과',
    statusOptions: [
      { value: 'incomplete', label: '미완료' },
      { value: 'completed', label: '완료' },
    ],
  },
]

export interface ListViewTabPanelProps {
  isOpen: boolean
  onClose: () => void
  /** 사이드패널 헤더 타이틀. 기본값: "목록" */
  title?: string
  /** 사이드패널 너비. 기본 1000 */
  width?: number
  /** z-index. 기본 40 (상세 패널이 위에 뜨도록 detail의 기본 50보다 낮춤) */
  zIndex?: number

  // ===== 사전 적용 필터 (lock 됨) =====
  workspaceId: string
  selectedBusinessId?: string
  selectedBusinessName?: string
  selectedDepartmentId?: string
  selectedDepartmentName?: string
  selectedMemberId?: string
  selectedMemberName?: string
  startDate?: Date | null
  endDate?: Date | null

  // ===== 콜백 =====
  isActionButtonClicked?: boolean
  onTaskItemClick?: (taskItem: TaskTreeNode) => void
  onTaskClick?: (task: TaskTreeNode) => void
  onBudgetItemClick?: (taskId: string) => void
  onPerformanceItemClick?: (taskId: string) => void
  onReportClick?: (task: TaskTreeNode) => void
}

const ListViewTabPanel: React.FC<ListViewTabPanelProps> = ({
  isOpen,
  onClose,
  title = '목록',
  width = 1000,
  zIndex = 40,
  workspaceId,
  selectedBusinessId,
  selectedBusinessName,
  selectedDepartmentId,
  selectedDepartmentName,
  selectedMemberId,
  selectedMemberName,
  startDate,
  endDate,
  isActionButtonClicked,
  onTaskItemClick,
  onTaskClick,
  onBudgetItemClick,
  onPerformanceItemClick,
  onReportClick,
}) => {
  const [activeTab, setActiveTab] = useState<ListViewTab>('task_item')
  // 워크스페이스 설정에서 세부실행과제 표시가 OFF 면 세부실행과제 탭을 뺀다
  const showTasks = useShowTasks()
  const visibleTabs = showTasks ? TABS : TABS.filter(t => t.key !== 'task')

  // ===== 패널-로컬 필터 state (탭 전환 시에도 유지, status만 예외) =====
  const [panelStatus, setPanelStatus] = useState('')
  const [panelPerformanceStatus, setPanelPerformanceStatus] = useState('')
  const [panelBusinessId, setPanelBusinessId] = useState('')
  const [panelBusinessName, setPanelBusinessName] = useState('')
  const [panelDepartmentId, setPanelDepartmentId] = useState('')
  const [panelDepartmentName, setPanelDepartmentName] = useState('')
  const [panelMemberId, setPanelMemberId] = useState('')
  const [panelMemberName, setPanelMemberName] = useState('')
  const [searchInput, setSearchInput] = useState('')
  const [panelSearchQuery, setPanelSearchQuery] = useState('')
  const [panelSearchTrigger, setPanelSearchTrigger] = useState(0)

  // 패널이 닫히면 로컬 필터/탭 초기화 (다음에 다시 열 때 깨끗한 상태)
  useEffect(() => {
    if (!isOpen) {
      setActiveTab('task_item')
      setPanelStatus('')
      setPanelPerformanceStatus('')
      setPanelBusinessId('')
      setPanelBusinessName('')
      setPanelDepartmentId('')
      setPanelDepartmentName('')
      setPanelMemberId('')
      setPanelMemberName('')
      setSearchInput('')
      setPanelSearchQuery('')
    }
  }, [isOpen])

  const handleTabChange = (tab: ListViewTab) => {
    if (tab === activeTab) return
    setActiveTab(tab)
    // 상태 옵션이 탭마다 다르므로 탭 전환 시 상태 필터 초기화
    setPanelStatus('')
    setPanelPerformanceStatus('')
  }

  const handleSearchSubmit = () => {
    setPanelSearchQuery(searchInput.trim())
    setPanelSearchTrigger(t => t + 1)
  }

  // 효과적 필터 = lock prop ?? panel-local state
  const effectiveStatus = panelStatus || undefined
  const effectiveBusinessId = selectedBusinessId ?? (panelBusinessId || undefined)
  const effectiveDepartmentId = selectedDepartmentId ?? (panelDepartmentId || undefined)
  const effectiveMemberId = selectedMemberId ?? (panelMemberId || undefined)

  const currentTab = TABS.find(t => t.key === activeTab) ?? TABS[0]

  return (
    <SidePanel isOpen={isOpen} onClose={onClose} title={title} width={width} zIndex={zIndex}>
      <div className="flex flex-col h-full">
        {/* 공유 필터 영역 */}
        <ListPanelFilterBar
          workspaceId={workspaceId}
          showStatus
          statusLabel={activeTab === 'task' ? '세부실행과제상태' : '상태'}
          statusOptions={currentTab.statusOptions}
          status={panelStatus}
          onStatusChange={setPanelStatus}
          performanceStatusOptions={activeTab === 'task' ? currentTab.statusOptions : undefined}
          performanceStatus={panelPerformanceStatus}
          onPerformanceStatusChange={setPanelPerformanceStatus}
          lockedBusinessId={selectedBusinessId}
          lockedBusinessName={selectedBusinessName}
          businessId={panelBusinessId}
          businessName={panelBusinessName}
          onBusinessChange={(id, name) => { setPanelBusinessId(id); setPanelBusinessName(name) }}
          lockedDepartmentId={selectedDepartmentId}
          lockedDepartmentName={selectedDepartmentName}
          departmentId={panelDepartmentId}
          departmentName={panelDepartmentName}
          onDepartmentChange={(id, name) => { setPanelDepartmentId(id); setPanelDepartmentName(name) }}
          lockedMemberId={selectedMemberId}
          lockedMemberName={selectedMemberName}
          memberId={panelMemberId}
          memberName={panelMemberName}
          onMemberChange={(id, name) => { setPanelMemberId(id); setPanelMemberName(name) }}
          searchInput={searchInput}
          onSearchInputChange={setSearchInput}
          onSearchSubmit={handleSearchSubmit}
        />

        {/* 탭 바 */}
        <div
          className="flex items-center gap-1 px-4"
          style={{ borderBottom: `1px solid ${designTokens.colors.gray[1]}` }}
        >
          {visibleTabs.map(tab => {
            const isActive = tab.key === activeTab
            return (
              <button
                key={tab.key}
                type="button"
                onClick={() => handleTabChange(tab.key)}
                style={{
                  fontFamily: designTokens.typography.fontFamily.sans[0],
                  fontSize: '14px',
                  fontWeight: isActive ? 700 : 400,
                  color: isActive ? designTokens.colors.primary.DEFAULT : designTokens.colors.gray[5],
                  padding: '12px 16px',
                  borderBottom: `2px solid ${isActive ? designTokens.colors.primary.DEFAULT : 'transparent'}`,
                  marginBottom: '-1px',
                  cursor: 'pointer',
                  background: 'transparent',
                }}
              >
                {tab.label}
              </button>
            )
          })}
        </div>

        {/* 활성 탭 목록 */}
        <div className="p-4 flex-1 overflow-hidden">
          {activeTab === 'task_item' && (
            <TaskItemsTable
              workspaceId={workspaceId}
              selectedBusinessId={effectiveBusinessId}
              selectedDepartmentId={effectiveDepartmentId}
              selectedMemberId={effectiveMemberId}
              isVisible={isOpen}
              startDate={startDate}
              endDate={endDate}
              searchQuery={panelSearchQuery}
              searchTrigger={panelSearchTrigger}
              status={effectiveStatus}
              onTaskItemClick={onTaskItemClick}
            />
          )}
          {activeTab === 'task' && (
            <TasksTable
              workspaceId={workspaceId}
              selectedBusinessId={effectiveBusinessId}
              selectedDepartmentId={effectiveDepartmentId}
              selectedMemberId={effectiveMemberId}
              isVisible={isOpen}
              startDate={startDate}
              endDate={endDate}
              searchQuery={panelSearchQuery}
              searchTrigger={panelSearchTrigger}
              status={effectiveStatus}
              performanceStatus={panelPerformanceStatus || undefined}
              isActionButtonClicked={isActionButtonClicked}
              onTaskClick={onTaskClick}
              onReportClick={onReportClick}
            />
          )}
          {activeTab === 'budget' && (
            <TaskBudgetsTable
              workspaceId={workspaceId}
              selectedBusinessId={effectiveBusinessId}
              selectedDepartmentId={effectiveDepartmentId}
              selectedMemberId={effectiveMemberId}
              isVisible={isOpen}
              startDate={startDate}
              endDate={endDate}
              searchQuery={panelSearchQuery}
              searchTrigger={panelSearchTrigger}
              status={effectiveStatus}
              isActionButtonClicked={isActionButtonClicked}
              onBudgetItemClick={onBudgetItemClick}
              onReportClick={onReportClick}
            />
          )}
          {activeTab === 'performance' && (
            <TaskPerformancesTable
              workspaceId={workspaceId}
              selectedBusinessId={effectiveBusinessId}
              selectedDepartmentId={effectiveDepartmentId}
              selectedMemberId={effectiveMemberId}
              isVisible={isOpen}
              startDate={startDate}
              endDate={endDate}
              searchQuery={panelSearchQuery}
              searchTrigger={panelSearchTrigger}
              status={effectiveStatus}
              isActionButtonClicked={isActionButtonClicked}
              onPerformanceItemClick={onPerformanceItemClick}
              onReportClick={onReportClick}
            />
          )}
        </div>
      </div>
    </SidePanel>
  )
}

export default ListViewTabPanel
